import * as Sentry from '@sentry/browser';
import {
  buildConfigInvalidFailure,
  buildConsentDeniedFailure,
  buildFailureResultFromError,
  buildResultCancelled,
  buildResultSuccess,
} from './submission-errors.js';

Sentry.init({
  beforeSend(event) {
    // Tag every event with its origin (library vs third-party frame) so we
    // can slice in Sentry. Library events go through unchanged. Third-party
    // events are now SAMPLED at 5% rather than dropped outright — without
    // some sample we have no signal on silent failures whose stacktraces
    // originate in browser internals (WASM compile errors, getUserMedia
    // rejections, fetch internals), which is exactly the cohort we need to
    // see to investigate customer-reported "camera UI loads, nothing
    // happens" failures.

    // Events without an exception (Sentry.captureMessage, breadcrumbs-only)
    // are explicit library calls — always treat as library so they reach
    // Sentry rather than being silently dropped by the third-party sampler.
    if (!event.exception?.values) {
      event.tags = { ...event.tags, source: 'library' };
      return event;
    }

    // WASM init failures (tagged in request.js) typically originate in
    // browser internals and would otherwise be classified as third-party
    // and sampled at 5% — but they're the exact cohort we instrumented
    // for, so always let them through and tag as library.
    const isWasmInit = event.tags?.area === 'wasm_init';
    const hasLibFrame = event.exception.values.some((exception) =>
      exception.stacktrace?.frames?.some((frame) =>
        frame.filename?.includes('inline/src'),
      ),
    );
    const treatAsLibrary = hasLibFrame || isWasmInit;
    event.tags = {
      ...event.tags,
      source: treatAsLibrary ? 'library' : 'third-party-frame',
    };
    if (!treatAsLibrary && Math.random() > 0.05) {
      return null;
    }
    return event;
  },
  dsn: 'https://82cc89f6d5a076c26d3a3cdc03a8d954@o1154186.ingest.us.sentry.io/4507143981236224',
  integrations: [
    // `apply-tag-if-contains-third-party-frames` (not the previous
    // `drop-error-if-contains-third-party-frames`) — the drop variant
    // discards events before our beforeSend gets a chance, which would
    // negate the 5% third-party sampling above. We tag instead and rely
    // on beforeSend for the final keep/drop decision.
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['smileid-web-client'],
      behaviour: 'apply-tag-if-contains-third-party-frames',
    }),
  ],
  tracesSampleRate: 0.01,
  tracePropagationTargets: [
    /^https:\/\/links\.usesmileid\.com/,
    /^https:\/\/links\.sandbox\.usesmileid\.com/,
    /^https:\/\/links\.dev\.usesmileid\.com/,
  ],
});

/**
 * A IdTypeVerificationMethod selection
 * @typedef {Object} IdTypeVerificationMethod
 * @property {string} country -
 * @property {string} id_type -
 * @property {string} verification_method - one of the smile identity
 * verification methods
 */

/**
* SmileIdentity - Creates an instance of the Smile Identity Web Integration
* @function
* @param { Object } config - the configuration object
* @param { string } config.token - token generated on the server side using the
* `get_web_token` method in one of our server-to-server libraries
* @param { string } config.callback_url - callback URL for responses
* @param { string } config.environment - one of `sandbox` or `production`
* @param { string } [config.product] - one of the product types,
* @param { Object[] } [config.id_types] - list of the id types and their
* verification_methods
* @param { Object[] } config.id_types[]. - list of the id types and their
* @param { string } config.id_type[].country - 
* @param { string } config.id_type[].id_type - list of the id types and their
* @param { string } config.id_type[].verification_method - one of
* SmileIdentity's verification_methods
* @param { Object } config.partner_details - partner details for customization
* @param { string } config.partner_details.name - name to display on the widget
* @param { string } config.partner_details.policy_url - URL for data privacy
	policy
* @param { string } config.partner_details.partner_id - partner_id for the
	organization
* @param { string } config.partner_details.theme_color - accent color for links in a css compliant color format
* @param { string } config.partner_details.logo_url - URL for a logo image,
	preferably in 1:1 aspect ratio
* @param { Object } [config.partner_params] - optional additional metadata for
* partner organization
* @param { Object } [config.translation] - optional translation configuration
* @param { string } [config.translation.language=en] - the language to use for the UI.
* Supported languages are: en, ar,
* @param { Object } [config.id_selection=all our [supported id types / countries]{@link https://docs.usesmileid.com/general/supported-id-types}] - a mapping of country code to a selection of supported id types
* e.g. { 'NG': ['BVN', 'NIN'] }
* @param { Object } [config.id_info] - a mapping of country code to id types with pre-filled field data.
* Takes precedence over id_selection when both are provided.
* If exactly 1 country + 1 ID type is provided, the selection screen is skipped.
* If all required fields pass validation, the input screen is also skipped.
 * Set `allow_modification: false` to skip the input screen when fields are provided but invalid (missing fields still show the form). Default: true.
 * e.g. { allow_modification: false, 'NG': { 'BVN': { id_number: '12345678901', first_name: 'John', last_name: 'Doe', dob: '1990-03-15' } } }
* @param { Object } [config.consent_required=none of our [supported id types / countries]{@link https://docs.usesmileid.com/general/supported-id-types}] - a mapping of country code to a selection of supported id types
* e.g. { 'NG': ['BVN', 'NIN'] }
* N.B.: DEPRECATED / no-op for the v3-migrated products (`biometric_kyc`,
	`ekyc_smartselfie`, `doc_verification`, `enhanced_document_verification`):
	the v3 endpoints require consent on every job, so the consent screen is
	always shown when `consent_information` is absent, regardless of this flag.
* @param { Object } [config.consent_information] - end-user consent obtained
* by the partner ahead of the session. Used by every v3 product
* (`smartselfie`, `authentication`, `biometric_kyc`, `ekyc_smartselfie`,
* `doc_verification`, `enhanced_document_verification`), whose submissions must
* carry consent: when provided (with `granted: true`), the in-flow consent
* screen is skipped; when omitted, the consent screen is shown to the user
* first. Consent bound into the partner's `smileid-token` takes precedence: the
* screen is skipped and this object is not sent.
* @param { boolean } config.consent_information.granted - must be `true`
* @param { string } config.consent_information.granted_at - ISO 8601 timestamp
* of when consent was obtained
* @param { string } [config.consent_information.notice_language] - 2-letter
* language code of the consent notice; defaults to the UI language
* @param { string } [config.consent_information.notice_privacy_policy_url] -
* URL of the privacy policy shown to the user; defaults to
* `partner_details.policy_url`
* @param { 'eu' | 'non-eu' } [config.consent_region] - overrides the
* region behaviour of the in-flow consent screen. `eu`: the consent
* checkbox starts un-ticked and blocks progression until ticked;
* `non-eu`: the checkbox starts pre-ticked. When omitted, the region is
* detected from the user's locale/timezone metadata, defaulting to the
* `eu` behaviour when ambiguous. Only relevant when the consent screen is
* shown (i.e. `consent_information` is not supplied).
* @param { Object } [config.user_details] - details of the end user being
* verified, for every v3 product's submission (`smartselfie`,
* `authentication`, `biometric_kyc`, `ekyc_smartselfie`, `doc_verification`,
* `enhanced_document_verification`): when provided, its shape is validated at
* init and the in-flow form is skipped; when omitted, the in-flow
* `<smileid-user-details>` form collects it from the user before capture.
* @param { string } config.user_details.given_names
* @param { string } config.user_details.last_name
* @param { string } [config.user_details.email] - at least one of `email` or
* `phone_number` is required
* @param { string } [config.user_details.phone_number] - E.164 format
*/
window.SmileIdentity = (function () {
  'use strict';

  function getSiteURL() {
    const currentScriptSrc = document.currentScript.src;
    const qualifiedURL = currentScriptSrc.split('script')[0];
    return qualifiedURL;
  }

  const innerConfig = {
    siteURL: getSiteURL(),
  };

  // A flow that has delivered a success/failure result never also resolves
  // `cancelled`. The iframe is a DOM singleton — only one flow is ever
  // in-flight — so a single dispatcher-scoped flag is enough to gate a
  // stray `cancelled` when the user closes the thank-you screen after the
  // result already fired.
  let terminalResultDelivered = false;

  function getIFrameURL(product) {
    Sentry.setTag('product', product);
    switch (product) {
      case 'biometric_kyc':
      case 'ekyc_smartselfie':
        return './../biometric-kyc.html';
      case 'enhanced_kyc':
        return './../ekyc.html';
      case 'authentication':
      case 'smartselfie':
        return './../smartselfie-auth.html';
      case 'doc_verification':
        return './../doc-verification.html';
      case 'enhanced_document_verification':
        return './../enhanced-document-verification.html';
      case 'residency_document_verification':
        return './../residency-document-verification.html';
      case 'e_signature':
        return './../e-signature.html';
      case 'basic_kyc':
      case 'identity_verification':
        return './../basic-kyc.html';
      case undefined:
        return './../product-selection.html';
      default:
        throw new Error(
          `SmileIdentity: ${product} is not currently supported in this integration`,
        );
    }
  }

  function createIframe(productName) {
    const iframe = document.createElement('iframe');

    // Forward the host page's `debug` flag into the iframe so the capture
    // components (which read their own window.location.search) can enable
    // the tuning panel and verbose telemetry.
    const debugSuffix = new URLSearchParams(window.location.search).has('debug')
      ? '?debug'
      : '';
    iframe.setAttribute(
      'src',
      `${innerConfig.siteURL}${getIFrameURL(productName)}${debugSuffix}`,
    );
    iframe.setAttribute('id', 'smile-identity-hosted-web-integration');
    iframe.setAttribute('name', 'smile-identity-hosted-web-integration');
    iframe.setAttribute('data-cy', 'smile-identity-hosted-web-integration');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute(
      'allow',
      'camera; geolocation; encrypted-media; fullscreen',
    );
    iframe.setAttribute('allowtransparency', 'true');

    iframe.style.cssText = `
			background-color: #F9F0E7;
			border: none;
			height: 100%;
			left: 0;
			position: fixed;
			top: 0;
			width: 100%;
			z-index: 999999;
		`;
    document.body.prepend(iframe);
  }

  function closeIFrame(config, userTriggered) {
    const iframe = document.querySelector(
      '#smile-identity-hosted-web-integration',
    );

    iframe?.remove();

    if (config.onClose && userTriggered) {
      config.onClose();
    }

    // A submission can never itself resolve to `cancelled`: only fire it
    // for the user-triggered close, and only if no success/failure result
    // was already delivered this flow.
    if (userTriggered && !terminalResultDelivered) {
      config.onResult?.(buildResultCancelled(config.product));
    }
  }

  function handleSuccess(config) {
    if (config.onSuccess) {
      config.onSuccess();
    }
  }

  function handleResult(config, payload) {
    handleSuccess(config);
    terminalResultDelivered = true;
    config.onResult?.(buildResultSuccess(payload.product, payload.payload));
  }

  function handleConsentRejection(config, tag) {
    if (config.onError) {
      config.onError(tag);
    }
    terminalResultDelivered = true;
    config.onResult?.(buildConsentDeniedFailure(tag, config.product));
  }

  function handleError(config, error) {
    if (config.onError) {
      config.onError(error);
    }
    terminalResultDelivered = true;
    // A product-selection session has no `config.product` — the product is
    // chosen inside the nested iframe — so prefer the one the wire carries.
    config.onResult?.(
      buildFailureResultFromError(error, error?.product || config.product),
    );
  }

  const requiredPartnerDetails = [
    'name',
    'logo_url',
    'partner_id',
    'policy_url',
    'theme_color',
  ];

  // Per-product capabilities that drive config validation.
  //  - `v3`: submits to a v3 endpoint, so consent + user_details apply and the
  //    shared v3 validation block runs (consent_information/user_details/
  //    consent_region).
  //  - `requiresPartnerDetails`: `partner_details` (incl. `policy_url`, needed
  //    for the required v3 consent notice, and branding for the consent
  //    screen) must be supplied. NOTE: newly required for `doc_verification`
  //    and `enhanced_document_verification` since their v3 migration — a
  //    breaking change for integrations that previously omitted it.
  const PRODUCT_CAPABILITIES = {
    smartselfie: { v3: true },
    authentication: { v3: true },
    biometric_kyc: { v3: true, requiresPartnerDetails: true },
    ekyc_smartselfie: { v3: true, requiresPartnerDetails: true },
    doc_verification: { v3: true, requiresPartnerDetails: true },
    enhanced_document_verification: { v3: true, requiresPartnerDetails: true },
    residency_document_verification: {
      v3: true,
      requiresPartnerDetails: true,
    },
  };

  function isConfigValid(config) {
    const capabilities = PRODUCT_CAPABILITIES[config.product] || {};
    if (!config.token)
      throw new Error(
        'SmileIdentity: Please provide your web token via the `token` attribute',
      );
    if (!config.callback_url)
      throw new Error(
        'SmileIdentity: Please provide a callback URL via the `callback_url` attribute',
      );
    if (!config.product && !config.id_types)
      throw new Error(
        'SmileIdentity: Please select a product via the `product` attribute.',
      );

    if (capabilities.requiresPartnerDetails) {
      if (!config.partner_details) {
        throw new Error(
          'SmileIdentity: Please provide Partner Details via the `partner_details` attribute',
        );
      }
      requiredPartnerDetails.forEach((param) => {
        if (!config.partner_details[param]) {
          throw new Error(
            `SmileIdentity: Please include ${param} in the "partner_details" object`,
          );
        }
      });
      Sentry.setTag('partner_id', config.partner_details?.partner_id);
    }

    if (
      config.document_capture_modes &&
      !Array.isArray(config.document_capture_modes)
    ) {
      throw new Error(
        'SmileIdentity: document_capture_modes must be an array containing one of `camera` or `upload`, or both',
      );
    }

    if (config.product === 'e_signature' && !config.document_ids) {
      throw new Error(
        'SmileIdentity: `document_ids` field is required for `e_signature` product type',
      );
    }

    if (
      config.product === 'e_signature' &&
      config.document_ids &&
      !Array.isArray(config.document_ids)
    ) {
      throw new Error(
        'SmileIdentity: `document_ids` must be an array containing ids of documents uploaded for `e_signature`',
      );
    }

    // v3 submissions must carry consent and user details. Both may come either
    // from the partner (pre-obtained, via `consent_information` /
    // `user_details`) or from an in-flow screen shown when the config carries
    // neither; if the partner does supply one, fail fast at init instead of
    // with a backend 400 after capture. Applies to every v3 product
    // (SmartSelfie/authentication, biometric_kyc, doc_verification, EDV).
    if (capabilities.v3) {
      if (
        config.consent_information?.granted === true &&
        !config.consent_information.granted_at
      ) {
        throw new Error(
          'SmileIdentity: Please include `granted_at` (ISO 8601 timestamp) in the `consent_information` object',
        );
      }
      if (config.user_details !== undefined) {
        if (
          !config.user_details.given_names ||
          !config.user_details.last_name
        ) {
          throw new Error(
            "SmileIdentity: Please provide the end user's `given_names` and `last_name` via the `user_details` attribute",
          );
        }
        if (!config.user_details.email && !config.user_details.phone_number) {
          throw new Error(
            'SmileIdentity: Please include at least one of `email` or `phone_number` in the `user_details` object',
          );
        }
      }
      // Fail fast on typos ('EU' is normalised downstream, 'europe' is not).
      if (
        config.consent_region !== undefined &&
        !['eu', 'non-eu'].includes(String(config.consent_region).toLowerCase())
      ) {
        throw new Error(
          "SmileIdentity: `consent_region` must be 'eu' or 'non-eu' when provided",
        );
      }
    }

    return true;
  }

  function publishConfigToIFrame(config) {
    const targetWindow = document.querySelector(
      "[name='smile-identity-hosted-web-integration']",
    ).contentWindow;
    config.source = 'SmileIdentity::Configuration';

    targetWindow.postMessage(JSON.stringify(config), '*');
  }

  function SmileIdentity(config) {
    try {
      const configIsValid = isConfigValid(config);
      Sentry.setTag('environment', config.environment);
      if (configIsValid) {
        terminalResultDelivered = false;
        createIframe(config.product);

        window.addEventListener(
          'message',
          (event) => {
            const tag = event.data.message || event.data;

            switch (tag) {
              case 'SmileIdentity::ChildPageReady':
                return publishConfigToIFrame(config);
              case 'SmileIdentity::Close':
                return closeIFrame(config, true);
              case 'SmileIdentity::Close::System':
                return closeIFrame(config, false);
              case 'SmileIdentity::Success':
                return handleSuccess(config);
              case 'SmileIdentity::Result':
                return handleResult(config, event.data);
              case 'SmileIdentity::ConsentDenied':
              case 'SmileIdentity::ConsentDenied::TOTP::ContactMethodsOutdated':
                return handleConsentRejection(config, event.data);
              case 'SmileIdentity::Error':
                return handleError(config, event.data);
              default:
                return undefined;
            }
          },
          false,
        );
      }
    } catch (error) {
      if (config.onError) {
        config.onError(error.message);
      }
      config.onResult?.(buildConfigInvalidFailure(error, config.product));
    }
  }

  return SmileIdentity;
})();
